import React, { useState, useEffect, useRef } from "react";
import "./dropdown.css";

export const Dropdown = ({
  label,
  options,
  value,
  onChange,
  required,
  placeholder = "Selecione uma opção",
  disabled = false,
  className = "",
  filterable = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value || "");
  const [activeIndex, setActiveIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const contentRef = useRef(null);
  const optionRefs = useRef([]);
  const [filterText, setFilterText] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);

  useEffect(() => {
    if (value !== undefined && value !== selectedValue) {
      setSelectedValue(value);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isOpen) {
        if (
          event.key === "Enter" ||
          event.key === " " ||
          event.key === "ArrowDown"
        ) {
          event.preventDefault();
          setIsOpen(true);
          setActiveIndex(0);
        }
        return;
      }

      // eslint-disable-next-line default-case
      switch (event.key) {
      case "Escape":
        event.preventDefault();
        setIsOpen(false);
        triggerRef.current?.focus();
        break;
      case "ArrowDown":
        event.preventDefault();
        setActiveIndex((prev) => {
          const newIndex = prev < filteredOptions.length - 1 ? prev + 1 : 0;
          optionRefs.current[newIndex]?.scrollIntoView({ block: "nearest" });
          return newIndex;
        });
        break;
      case "ArrowUp":
        event.preventDefault();
        setActiveIndex((prev) => {
          const newIndex = prev > 0 ? prev - 1 : filteredOptions.length - 1;
          optionRefs.current[newIndex]?.scrollIntoView({ block: "nearest" });
          return newIndex;
        });
        break;
      case "Enter":
        if (activeIndex >= 0 && filteredOptions[activeIndex]) {
          event.preventDefault();
          handleSelect(filteredOptions[activeIndex]);
        }
        break;
      case "Tab":
        setIsOpen(false);
        return;
      case "Backspace":
        if (filterable) {
          const newFilterText = filterText.slice(0, -1);
          setFilterText(newFilterText);

          if (newFilterText.length === 0) {
            setFilteredOptions(options);
          } else {
            filterOptions(newFilterText);
          }
        }
        break;
      default:
        if (
          filterable &&
            event.key.length === 1 &&
            !event.ctrlKey &&
            !event.altKey &&
            !event.metaKey
        ) {
          const newFilterText = filterText + event.key;
          setFilterText(newFilterText);
          filterOptions(newFilterText);
        }
        break;
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeIndex, options, filterText]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        isOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen]);

  const updatePosition = () => {
    if (!contentRef.current || !triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const contentRect = contentRef.current.getBoundingClientRect();

    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const contentHeight = contentRect.height;

    const position =
      spaceBelow < contentHeight && spaceAbove > spaceBelow ? "top" : "bottom";

    const left = triggerRect.left;
    let top;

    if (position === "bottom") {
      top = triggerRect.bottom;
      contentRef.current.style.transformOrigin = "top";
    } else {
      top = triggerRect.top - contentHeight;
      contentRef.current.style.transformOrigin = "bottom";
    }

    contentRef.current.style.width = `${triggerRect.width}px`;
    contentRef.current.style.left = `${left}px`;
    contentRef.current.style.top = `${top}px`;
  };


  const filterOptions = (text) => {
    if (text.trim() === "") {
      setFilteredOptions(options);
    } else {
      const normalize = str =>
        str
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .trim();

      const filtered = options.filter((option) => {
        if (!text.trim()) return false;
        return normalize(option.label).includes(normalize(text));
      });
      setFilteredOptions(filtered);
    }

    setActiveIndex(0);
  };

  const handlePaste = (event) => {
    if (filterable) {
      const pastedText = event.clipboardData.getData("text");
      const newFilterText = filterText + pastedText;
      setFilterText(newFilterText);
      filterOptions(newFilterText);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    updatePosition();

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      if (selectedValue) {
        const selectedIndex = filteredOptions.findIndex(
          (opt) => opt.value === selectedValue
        );
        if (selectedIndex >= 0) {
          setActiveIndex(selectedIndex);
          optionRefs.current[selectedIndex]?.scrollIntoView({
            block: "nearest",
          });
        }
      } else {
        setActiveIndex(0);
      }
    } else {
      setActiveIndex(-1);
      setFilterText("");
      setFilteredOptions(options);
    }
  }, [isOpen, selectedValue, options, filteredOptions]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen && filterable) {
        setFilterText("");
        setFilteredOptions(options);

        setTimeout(() => {
          triggerRef.current?.focus();
        }, 0);
      }
    }
  };

  const handleSelect = (option) => {
    setSelectedValue(option);
    onChange?.(option);
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const selectedLabel =
    options.find((option) => option.value === selectedValue?.value)?.label ||
    placeholder;

  useEffect(() => {
    optionRefs.current = optionRefs.current.slice(0, filteredOptions.length);
  }, [filteredOptions]);

  return (
    <div className={`dropdown-container ${className}`} ref={dropdownRef}>
      {label && (
        <label
          className="dropdown-label"
          id={`dropdown-label-${label.replace(/\s+/g, "-").toLowerCase()}`}
        >
          {label}{required && <span className="required-indicator">*</span>}
        </label>
      )}

      <div
        ref={triggerRef}
        className={`dropdown-field ${isOpen ? "open" : ""} ${disabled ? "disabled" : ""}`}
        onClick={handleToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
            e.preventDefault();
            setIsOpen(true);
          }
        }}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-labelledby={
          label
            ? `dropdown-label-${label.replace(/\s+/g, "-").toLowerCase()}`
            : undefined
        }
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
      >
        <div className="dropdown-selected">
          <span className={isOpen && filterText && "dropdown-filter-text"} onPaste={handlePaste}>{isOpen && filterText ? filterText : selectedLabel}</span>
        </div>
        <div className="dropdown-arrow">
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M2.5 4.5L6 8L9.5 4.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {isOpen && (
        <ul
          ref={contentRef}
          className="dropdown-options"
          role="listbox"
          aria-labelledby={
            label
              ? `dropdown-label-${label.replace(/\s+/g, "-").toLowerCase()}`
              : undefined
          }
          tabIndex={-1}
        >
          {filteredOptions.length === 0 ? (
            <li className="dropdown-no-results">Nenhum resultado encontrado</li>
          ) : (
            filteredOptions.map((option, index) => (
              <li
                ref={(el) => {
                  optionRefs.current[index] = el;
                  return undefined;
                }}
                key={option.value+option.label+option.amount}
                className={`dropdown-option ${option.value === selectedValue?.value ? "selected" : ""} ${index === activeIndex ? "active" : ""}`}
                onClick={() => handleSelect(option)}
                onMouseEnter={() => setActiveIndex(index)}
                role="option"
                aria-selected={option.value === selectedValue?.value}
                tabIndex={-1}
              >
                {option.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

export default Dropdown;